import { Modal } from 'app-components';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { ConditionalRendering } from '@components/common/conditional-rendering';
import { ModalContextType } from '@contexts/modalContext';
import { formatDateString, formatToLocale } from '@utils/dates/dateMethods';

import { DATE_FORMATS } from '@constants/common/dateTimeValues';
import { DESC_FORMAT_13 } from '@utils/dates/dateFormats.utils';

import { formatDate } from '@utils/dates/date.utils';
import { getCurrency } from '@utils/tools/currencyTools';
import { IEnrollmentPremiumHistoryModal, IEnrollmentSnapshotResponse } from '../common/types';
import HouseholdInfoTable from './HouseholdInfoTable';
import { MODAL_KEYS } from './modalKeys';
import { StyledBoldUnderline, StyledColumn, StyledContainer, StyledModalWrapper, StyledRow } from './ModalStyles';

interface IModalProps extends ModalContextType {
    enrollmentShanpshot: IEnrollmentSnapshotResponse;
    premiumHistory: IEnrollmentPremiumHistoryModal;
}

const CancelTerminateModal = ({ closeModal, enrollmentShanpshot, premiumHistory: { clearState } }: IModalProps) => {
    const intl = useIntl();
    const { language } = useSelector((state: any) => state?.common?.environment);

    const { eventCreationDate, enrollmentDataDTOList, enrolleeDataDtoList } = useMemo(() => {
        const eventDate = enrollmentShanpshot?.enrollmentDataDTOList?.[0]?.eventCreationDate;
        const eventCreationDate = eventDate ? formatToLocale(eventDate, language) : '';
        const enrolleeDataDtoList = enrollmentShanpshot?.enrollmentDataDTOList?.[0]?.enrolleeDataDtoList || [];
        const { enrollmentDataDTOList } = enrollmentShanpshot || { enrollmentDataDTOList: [] };
        return { eventCreationDate, enrollmentDataDTOList, enrolleeDataDtoList };
    }, [enrollmentShanpshot, language]);

    const noData = intl.formatMessage({ id: 'member.enrollment.modal.cancelTerminate.NA' });
    const hasAptcAmpount = enrollmentDataDTOList.some(
        enrollment => enrollment.aptcAmount != null,
    );

    const handleClose = e => {
        closeModal(MODAL_KEYS.CANCEL_TERMINATE);
        clearState();
        e.preventDefault();
    };

    return (
        <StyledModalWrapper data-testid="modal-wrapper">
            <Modal id={MODAL_KEYS.CANCEL_TERMINATE} data-testid="modal">
                <Modal.Header
                    id="cancelTerminateModalTitle"
                    titleId={intl.formatMessage(
                        { id: 'member.enrollment.modal.cancelTerminate.title' },
                        { eventCreationDate: formatDate(eventCreationDate, DESC_FORMAT_13) },
                    )}
                    onHide={handleClose}
                    data-testid="modal-header"
                />
                <Modal.Body data-testid="modal-body">
                    {enrollmentDataDTOList.map(enrollment => (
                        <StyledContainer key={enrollment.CMSPlanID} data-testid="container">
                            <StyledRow>
                                <StyledColumn data-testid="column-about-transaction">
                                    <StyledBoldUnderline data-testid="about-transaction">
                                        {intl.formatMessage({
                                            id: 'member.enrollment.modal.cancelTerminate.aboutTransaction',
                                        })}
                                    </StyledBoldUnderline>
                                </StyledColumn>
                                <StyledColumn data-testid="column-enrollment-ids">
                                    <StyledBoldUnderline data-testid="enrollmentIds">
                                        {intl.formatMessage({
                                            id: 'member.enrollment.modal.cancelTerminate.enrollmentIds',
                                        })}
                                    </StyledBoldUnderline>
                                </StyledColumn>
                            </StyledRow>
                            <StyledRow>
                                <StyledColumn data-testid="subscriberEvent">
                                    {intl.formatMessage({
                                        id: 'member.enrollment.modal.cancelTerminate.subscriberEvent',
                                    })}
                                </StyledColumn>
                                <StyledColumn data-testid="cancelTerminate">
                                    <ConditionalRendering isTrue={!!enrollment.eventType}>
                                        {enrollment.eventType}
                                    </ConditionalRendering>
                                    <ConditionalRendering isTrue={!enrollment.eventType}>
                                        {intl.formatMessage({ id: 'member.enrollment.modal.cancelTerminate.NA' })}
                                    </ConditionalRendering>
                                </StyledColumn>
                                <StyledColumn data-testid="exchangePolicyId">
                                    {intl.formatMessage({
                                        id: 'member.enrollment.modal.cancelTerminate.exchangePolicyId',
                                    })}
                                </StyledColumn>
                                <StyledColumn data-testid="enrollmentId">
                                    <ConditionalRendering isTrue={!!enrollment.enrollmentId}>
                                        {enrollment.enrollmentId}
                                    </ConditionalRendering>
                                    <ConditionalRendering isTrue={!enrollment.enrollmentId}>
                                        {noData}
                                    </ConditionalRendering>
                                </StyledColumn>
                            </StyledRow>
                            <StyledRow>
                                <StyledColumn data-testid="subscriberMaintenanceReason">
                                    {intl.formatMessage({
                                        id: 'member.enrollment.modal.cancelTerminate.subscriberMaintenanceReason',
                                    })}
                                </StyledColumn>
                                <StyledColumn data-testid="eventReason">
                                    <ConditionalRendering isTrue={!!enrollment.eventReason}>
                                        {enrollment.eventReason}
                                    </ConditionalRendering>
                                    <ConditionalRendering isTrue={!enrollment.eventReason}>
                                        {noData}
                                    </ConditionalRendering>
                                </StyledColumn>
                                <StyledColumn data-testid="cmsPlanId">
                                    {intl.formatMessage({ id: 'member.enrollment.modal.cancelTerminate.cmsPlanId' })}
                                </StyledColumn>
                                <StyledColumn data-testid="CMSPlanID">
                                    <ConditionalRendering isTrue={!!enrollment.CMSPlanID}>
                                        {enrollment.CMSPlanID}
                                    </ConditionalRendering>
                                    <ConditionalRendering isTrue={!enrollment.CMSPlanID}>{noData}</ConditionalRendering>
                                </StyledColumn>
                            </StyledRow>
                            <StyledRow>
                                <StyledColumn data-testid="date">
                                    {intl.formatMessage({ id: 'member.enrollment.modal.cancelTerminate.date' })}
                                </StyledColumn>
                                <StyledColumn data-testid="eventCreationDate">
                                    <ConditionalRendering isTrue={!!enrollment.eventCreationDate}>
                                        {formatToLocale(enrollment.eventCreationDate, language)}
                                    </ConditionalRendering>
                                    <ConditionalRendering isTrue={!enrollment.eventCreationDate}>
                                        {noData}
                                    </ConditionalRendering>
                                </StyledColumn>
                                <StyledColumn></StyledColumn>
                                <StyledColumn></StyledColumn>
                            </StyledRow>
                            <StyledRow>
                                <StyledColumn data-testid="createdBy">
                                    {intl.formatMessage({ id: 'member.enrollment.modal.cancelTerminate.createdBy' })}
                                </StyledColumn>
                                <StyledColumn data-testid="eventCreatedBy">
                                    {enrollment.eventCreatedBy} ({enrollment.userRole})
                                </StyledColumn>
                                <StyledColumn></StyledColumn>
                                <StyledColumn></StyledColumn>
                            </StyledRow>
                            <StyledRow>
                                <StyledColumn data-testid="planInformation">
                                    <StyledBoldUnderline>
                                        {intl.formatMessage({
                                            id: 'member.enrollment.modal.cancelTerminate.planInformation',
                                        })}
                                    </StyledBoldUnderline>
                                </StyledColumn>
                                <StyledColumn data-testid="monthlyPayment">
                                    <StyledBoldUnderline>
                                        {intl.formatMessage({
                                            id: 'member.enrollment.modal.cancelTerminate.monthlyPayment',
                                        })}
                                    </StyledBoldUnderline>
                                </StyledColumn>
                            </StyledRow>
                            <StyledRow>
                                <StyledColumn data-testid="carrier">
                                    {intl.formatMessage({ id: 'member.enrollment.modal.cancelTerminate.carrier' })}
                                </StyledColumn>
                                <StyledColumn data-testid="carrierName">
                                    <ConditionalRendering isTrue={!!enrollment.carrierName}>
                                        {enrollment.carrierName}
                                    </ConditionalRendering>
                                    <ConditionalRendering isTrue={!enrollment.carrierName}>
                                        {noData}
                                    </ConditionalRendering>
                                </StyledColumn>
                                <StyledColumn data-testid="financialEffectiveDate">
                                    {intl.formatMessage({
                                        id: 'member.enrollment.modal.cancelTerminate.financialEffectiveDate',
                                    })}
                                </StyledColumn>
                                <StyledColumn data-testid="financialEffectiveDateValue">
                                    <ConditionalRendering isTrue={!!enrollment.financialEffectiveDate}>
                                        {formatToLocale(enrollment.financialEffectiveDate, language)}
                                    </ConditionalRendering>
                                    <ConditionalRendering isTrue={!enrollment.financialEffectiveDate}>
                                        {noData}
                                    </ConditionalRendering>
                                </StyledColumn>
                            </StyledRow>
                            <StyledRow>
                                <StyledColumn data-testid="planName">
                                    {intl.formatMessage({ id: 'member.enrollment.modal.cancelTerminate.planName' })}
                                </StyledColumn>
                                <StyledColumn data-testid="planNameValue">
                                    <ConditionalRendering isTrue={!!enrollment.planName}>
                                        {enrollment.planName}
                                    </ConditionalRendering>
                                    <ConditionalRendering isTrue={!enrollment.planName}>{noData}</ConditionalRendering>
                                </StyledColumn>
                                <StyledColumn data-testid="grossPremiumAmount">
                                    {intl.formatMessage({
                                        id: 'member.enrollment.modal.cancelTerminate.grossPremiumAmount',
                                    })}
                                </StyledColumn>
                                <StyledColumn data-testid="grossPremiumAmt">
                                    <ConditionalRendering isTrue={!!enrollment.grossPremiumAmt}>
                                        {getCurrency(enrollment.grossPremiumAmt)}
                                    </ConditionalRendering>
                                    <ConditionalRendering isTrue={!enrollment.grossPremiumAmt}>
                                        {noData}
                                    </ConditionalRendering>
                                </StyledColumn>
                            </StyledRow>
                            <StyledRow>
                                <StyledColumn data-testid="effectiveDates">
                                    {intl.formatMessage({
                                        id: 'member.enrollment.modal.cancelTerminate.effectiveDates',
                                    })}
                                </StyledColumn>
                                <StyledColumn data-testid="effectiveDatesValue">
                                    {formatDateString(
                                        enrollment.enrollmentBenefitEffectiveStartDate,
                                        DATE_FORMATS.MM_DD_YYYY,
                                    )}{' '}
                                    -{' '}
                                    {formatDateString(
                                        enrollment.enrollmentBenefitEffectiveEndDate,
                                        DATE_FORMATS.MM_DD_YYYY,
                                    )}
                                </StyledColumn>
                                <StyledColumn data-testid="electedFederalAptcAmount">
                                    {intl.formatMessage({
                                        id: 'member.enrollment.modal.cancelTerminate.electedFederalAptcAmount',
                                    })}
                                </StyledColumn>
                                <StyledColumn data-testid="aptcAmount">
                                    <ConditionalRendering isTrue={hasAptcAmpount}>
                                        {getCurrency(enrollment.aptcAmount)}
                                    </ConditionalRendering>
                                    <ConditionalRendering isTrue={!hasAptcAmpount}>
                                        {noData}
                                    </ConditionalRendering>
                                </StyledColumn>
                            </StyledRow>
                            <StyledRow>
                                <StyledColumn data-testid="coverageTier">
                                    {intl.formatMessage({ id: 'member.enrollment.modal.cancelTerminate.coverageTier' })}
                                </StyledColumn>
                                <StyledColumn data-testid="planLevel">{enrollment.planLevel || noData}</StyledColumn>

                                <ConditionalRendering
                                    isTrue={
                                        enrollment.isStateSubsidyEnabled && enrollment.stateSubsidyAmount !== undefined
                                    }
                                >
                                    <StyledColumn data-testid="stateSubsidy">
                                        {intl.formatMessage({
                                            id: 'member.enrollment.modal.cancelTerminate.stateSubsidy',
                                        })}
                                    </StyledColumn>
                                    <StyledColumn data-testid="stateSubsidyAmount">
                                        {getCurrency(enrollment.stateSubsidyAmount)}
                                    </StyledColumn>
                                </ConditionalRendering>

                                <ConditionalRendering
                                    isTrue={
                                        !enrollment.isStateSubsidyEnabled &&
                                        enrollment.statePremiumCreditAmt !== undefined
                                    }
                                >
                                    <StyledColumn data-testid="premiumCredit">
                                        {intl.formatMessage({
                                            id: 'member.enrollment.modal.cancelTerminate.premiumCredit',
                                        })}
                                    </StyledColumn>
                                    <StyledColumn data-testid="statePremiumCreditAmt">
                                        {getCurrency(enrollment.statePremiumCreditAmt)}
                                    </StyledColumn>
                                </ConditionalRendering>

                                <ConditionalRendering
                                    isTrue={
                                        !enrollment.isStateSubsidyEnabled &&
                                        enrollment.statePremiumCreditAmt === undefined
                                    }
                                >
                                    <StyledColumn data-testid="netPremium">
                                        {intl.formatMessage({
                                            id: 'member.enrollment.modal.cancelTerminate.netPremium',
                                        })}
                                    </StyledColumn>
                                    <StyledColumn data-testid="netPremiumAmt">
                                        {getCurrency(enrollment.netPremiumAmt)}
                                    </StyledColumn>
                                </ConditionalRendering>
                            </StyledRow>

                            <ConditionalRendering
                                isTrue={
                                    enrollment.isStateSubsidy2Enabled && enrollment.stateSubsidy2Amount !== undefined
                                }
                            >
                                <StyledRow>
                                    <StyledColumn></StyledColumn>
                                    <StyledColumn></StyledColumn>
                                    <StyledColumn data-testid="stateSubsidy2">
                                        {intl.formatMessage({
                                            id: 'member.enrollment.modal.cancelTerminate.stateSubsidy2',
                                        })}
                                    </StyledColumn>
                                    <StyledColumn data-testid="stateSubsidy2Amount">
                                        {getCurrency(enrollment.stateSubsidy2Amount)}
                                    </StyledColumn>
                                </StyledRow>
                            </ConditionalRendering>

                            <ConditionalRendering
                                isTrue={
                                    enrollment.isStateSubsidyEnabled &&
                                    enrollment.stateSubsidyAmount !== undefined &&
                                    enrollment.statePremiumCreditAmt !== undefined
                                }
                            >
                                <StyledRow>
                                    <StyledColumn></StyledColumn>
                                    <StyledColumn></StyledColumn>
                                    <StyledColumn data-testid="premiumCredit">
                                        {intl.formatMessage({
                                            id: 'member.enrollment.modal.cancelTerminate.premiumCredit',
                                        })}
                                    </StyledColumn>
                                    <StyledColumn data-testid="statePremiumCreditAmt">
                                        {getCurrency(enrollment.statePremiumCreditAmt)}
                                    </StyledColumn>
                                </StyledRow>
                            </ConditionalRendering>

                            <ConditionalRendering
                                isTrue={
                                    (enrollment.isStateSubsidyEnabled && enrollment.stateSubsidyAmount !== undefined) ||
                                    enrollment.statePremiumCreditAmt !== undefined
                                }
                            >
                                <StyledRow>
                                    <StyledColumn></StyledColumn>
                                    <StyledColumn></StyledColumn>
                                    <StyledColumn data-testid="netPremium">
                                        {intl.formatMessage({
                                            id: 'member.enrollment.modal.cancelTerminate.netPremium',
                                        })}
                                    </StyledColumn>
                                    <StyledColumn data-testid="netPremiumAmt">
                                        {getCurrency(enrollment.netPremiumAmt)}
                                    </StyledColumn>
                                </StyledRow>
                            </ConditionalRendering>

                            <ConditionalRendering isTrue={!!(enrollment.agentName && enrollment?.agentTpaNumber)}>
                                <>
                                    <StyledRow>
                                        <StyledColumn data-testid="agentInformation">
                                            <StyledBoldUnderline>
                                                {intl.formatMessage({
                                                    id: 'member.enrollment.modal.cancelTerminate.agentInformation',
                                                })}
                                            </StyledBoldUnderline>
                                        </StyledColumn>
                                    </StyledRow>
                                    <StyledRow>
                                        <StyledColumn data-testid="agentName">
                                            {intl.formatMessage({
                                                id: 'member.enrollment.modal.cancelTerminate.agentName',
                                            })}
                                        </StyledColumn>
                                        <StyledColumn data-testid="agentNameValue">{enrollment.agentName}</StyledColumn>
                                        <StyledColumn></StyledColumn>
                                        <StyledColumn></StyledColumn>
                                    </StyledRow>
                                    <StyledRow>
                                        <StyledColumn data-testid="agentTpaNumber">
                                            {intl.formatMessage({
                                                id: 'member.enrollment.modal.cancelTerminate.agentTpaNumber',
                                            })}
                                        </StyledColumn>
                                        <StyledColumn data-testid="agentTpaNumberValue">
                                            {enrollment.agentTpaNumber}
                                        </StyledColumn>
                                        <StyledColumn></StyledColumn>
                                        <StyledColumn></StyledColumn>
                                    </StyledRow>
                                </>
                            </ConditionalRendering>

                            <HouseholdInfoTable enrollees={enrolleeDataDtoList} data-testid="household-info-table" />
                        </StyledContainer>
                    ))}
                </Modal.Body>
            </Modal>
        </StyledModalWrapper>
    );
};

export default CancelTerminateModal;
